import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import toast from 'react-hot-toast';
import { Camera, CameraOff, Loader2, QrCode, CheckCircle2 } from 'lucide-react';
import { qrAttendanceAPI } from '../services/api';

function extractToken(raw) {
  const value = String(raw || '').trim();
  if (value.toLowerCase().startsWith('eams-qr:')) {
    return value.slice('eams-qr:'.length).trim();
  }
  return value;
}

const SCANNER_ID = 'eams-web-qr-reader';

const ScanQr = () => {
  const scannerRef = useRef(null);
  const lockRef = useRef(false);
  const [cameraOn, setCameraOn] = useState(false);
  const [starting, setStarting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [cameraError, setCameraError] = useState('');

  const stopCamera = useCallback(async () => {
    const scanner = scannerRef.current;
    scannerRef.current = null;
    if (!scanner) {
      setCameraOn(false);
      return;
    }
    try {
      if (scanner.isScanning) {
        await scanner.stop();
      }
      await scanner.clear();
    } catch {
      // ignore stop/clear races when unmounting
    }
    setCameraOn(false);
  }, []);

  const submitToken = useCallback(async (raw) => {
    const token = extractToken(raw);
    if (!token || token.length < 8) {
      toast.error('Scan a valid station QR from the office screen.');
      return;
    }
    if (lockRef.current) return;

    try {
      lockRef.current = true;
      setBusy(true);
      const res = await qrAttendanceAPI.scan(token);
      const action = res.data?.action === 'check-out' ? 'checked out' : 'checked in';
      setLastResult({
        action: res.data?.action || 'check-in',
        message: `You ${action} via QR.`,
        at: new Date().toISOString(),
      });
      toast.success(`You ${action} via QR.`);
      await stopCamera();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'QR scan failed.');
    } finally {
      setBusy(false);
      setTimeout(() => {
        lockRef.current = false;
      }, 1800);
    }
  }, [stopCamera]);

  const startCamera = useCallback(async () => {
    if (starting || cameraOn) return;
    setCameraError('');
    setStarting(true);
    setLastResult(null);

    try {
      await stopCamera();
      const scanner = new Html5Qrcode(SCANNER_ID);
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: 'environment' },
        {
          fps: 8,
          qrbox: { width: 240, height: 240 },
          aspectRatio: 1,
        },
        (decoded) => {
          if (decoded) void submitToken(decoded);
        },
        () => {},
      );
      setCameraOn(true);
    } catch (e) {
      scannerRef.current = null;
      setCameraOn(false);
      setCameraError(
        e?.message ||
          'Camera could not be started. Allow camera access in your browser, then try again.',
      );
    } finally {
      setStarting(false);
    }
  }, [cameraOn, starting, stopCamera, submitToken]);

  useEffect(() => {
    return () => {
      void stopCamera();
    };
  }, [stopCamera]);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="page-title">Scan QR</h1>
        <p className="page-subtitle">
          Point your camera at the office station QR to check in or check out.
        </p>
      </div>

      <div className="panel p-4 sm:p-6">
        <div className="mx-auto max-w-md space-y-4">
          <div
            id={SCANNER_ID}
            className={`overflow-hidden rounded-lg bg-gray-900 ${
              cameraOn ? 'min-h-[240px] sm:min-h-[280px]' : 'min-h-0'
            }`}
          />

          {!cameraOn && (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-10 sm:px-6 sm:py-12 text-center">
              <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-white ring-1 ring-gray-200">
                <QrCode className="h-6 w-6 text-gray-700" />
              </div>
              <p className="text-sm font-medium text-gray-900">Ready to scan</p>
              <p className="mt-1 text-[13px] text-gray-500">
                Start the camera and hold it over the station QR code.
              </p>
            </div>
          )}

          {cameraError && (
            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-950">
              {cameraError}
            </p>
          )}

          {lastResult && (
            <div className="flex items-start gap-3 rounded-md border border-gray-200 bg-white px-3 py-3">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-gray-900" />
              <div>
                <p className="text-sm font-medium text-gray-900">{lastResult.message}</p>
                <p className="mt-0.5 text-[12px] text-gray-500">
                  {lastResult.action === 'check-out' ? 'Check-out' : 'Check-in'} recorded
                </p>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {!cameraOn ? (
              <button
                type="button"
                onClick={() => void startCamera()}
                disabled={starting || busy}
                className="btn-primary w-full sm:w-auto"
              >
                {starting ? (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Camera className="mr-1.5 h-3.5 w-3.5" />
                )}
                {starting ? 'Starting…' : 'Start camera'}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => void stopCamera()}
                disabled={busy}
                className="btn-outline w-full sm:w-auto"
              >
                <CameraOff className="mr-1.5 h-3.5 w-3.5" />
                Stop camera
              </button>
            )}
            {busy && (
              <span className="inline-flex items-center text-[13px] text-gray-500">
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                Recording attendance…
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScanQr;
